import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from "react";
import { useAuth } from "./useAuth";
import { useSchool } from "./useSchool";
import { useParams } from "next/navigation";
import {
  AUTO_PLAY_KEY,
  JUMP_TO_LAST_WATCH_ENABLE_KEY,
} from "@/consts/settings";
import { settingsRepository } from "./firestore/repositories/SettingsRepository";
import { useEffectOnce } from "./useEffectOnce";
import { progressRepository } from "./firestore/repositories/ProgressRepository";
import { ProgressAndLastSeenEntry } from "@/types/progressAndLastSeen";
import { lastSeenRepository } from "./firestore/repositories/LastSeenRepository";

const VideoPlayerContext = createContext<VideoPlayerContextType | undefined>(
  undefined
);

const localLog = (...args: unknown[]) => {
  if (process.env.NEXT_PUBLIC_ENV === "development") {
    console.log("[VideoPlayerProvider]", ...args);
  }
};

type ChangeSettingKey =
  | typeof AUTO_PLAY_KEY
  | typeof JUMP_TO_LAST_WATCH_ENABLE_KEY;

interface VideoPlayerContextType {
  autoPlay: boolean;
  jumpToLastWatchEnable: boolean;
  changeSetting: (key: ChangeSettingKey, value: boolean) => void;
  blockChangeTrigger: "next" | "prev" | undefined;
  setBlockChangeTrigger: (value: "next" | "prev" | undefined) => void;
  handleBlockChange: (action: "next" | "prev") => void;
  sendProgressEvent: (
    contentBlockId: string,
    time: number,
    seen?: boolean
  ) => void;
  triggerJumpToTime: number | undefined;
  setTriggerJumpToTime: Dispatch<SetStateAction<number | undefined>>;
  progressData: ProgressAndLastSeenEntry[] | undefined;
  setProgressData: Dispatch<
    SetStateAction<ProgressAndLastSeenEntry[] | undefined>
  >;
  changeSeenStatus: (contentBlockId: string, newStatus: boolean) => void;
  lastSeenBlockId: string | undefined;
}

export function VideoPlayerProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { school } = useSchool();
  const { id: courseId } = useParams();
  const [jumpToLastWatchEnable, setJumpToLastWatchEnable] =
    useState<boolean>(false);
  const [autoPlay, setAutoPlay] = useState<boolean>(false);
  const [triggerJumpToTime, setTriggerJumpToTime] = useState<
    number | undefined
  >(undefined);
  const [blockChangeTrigger, setBlockChangeTrigger] = useState<
    "next" | "prev" | undefined
  >(undefined);
  const [progressData, setProgressData] = useState<
    ProgressAndLastSeenEntry[] | undefined
  >(undefined);

  const [lastSeenBlockId, setLastSeenBlockId] = useState<string | undefined>(
    undefined
  );

  const loadSettings = async () => {
    if (!user) return;
    const settings = await settingsRepository.getSettingsByUserId(user.uid);
    if (settings) {
      setAutoPlay(settings[AUTO_PLAY_KEY] || false);
      setJumpToLastWatchEnable(
        settings[JUMP_TO_LAST_WATCH_ENABLE_KEY] || false
      );
    }
  };

  const changeSetting = (key: ChangeSettingKey, value: boolean) => {
    if (!user || !user.uid) return;
    localLog("Changing setting:", key, value);
    if (key === AUTO_PLAY_KEY) {
      setAutoPlay(value);
    } else if (key === JUMP_TO_LAST_WATCH_ENABLE_KEY) {
      setJumpToLastWatchEnable(value);
    }
    settingsRepository.setUserSetting(user.uid, key, value);
  };

  // Load settings on mount
  useEffectOnce(() => {
    loadSettings();
  }, []);

  const handleBlockChange = (action: "next" | "prev") => {
    setBlockChangeTrigger(action);

    setTimeout(() => {
      setBlockChangeTrigger(undefined);
    }, 500);
  };

  const getCourseProgress = useCallback(async () => {
    if (!user || !courseId) {
      localLog("User or courseId not available yet");
      return;
    }
    if (progressData) {
      localLog("Progress data already loaded");
      return;
    }
    const courseProgress = await progressRepository.getCourseProgress(
      user.uid,
      courseId as string
    );

    if (!courseProgress || courseProgress.length === 0) {
      localLog("No progress data found for user:", user.uid);
    }
    setProgressData(courseProgress);
  }, [user, courseId, setProgressData, progressData]);

  // Fetch user progress when user or courseId changes
  useEffectOnce(() => {
    getCourseProgress();
  }, [user, courseId, getCourseProgress]);

  const sendProgressEvent = async (
    contentBlockId: string,
    currentTime: number,
    seen?: boolean
  ) => {
    if (!user || !courseId || !school) {
      localLog("Cannot send progress event: missing user, school or courseId");
      return;
    }

    if (currentTime < 0) {
      localLog("Invalid time value:", currentTime);
      return;
    }
    localLog("Sending time event:", currentTime, "s");

    const userId = user.uid;

    progressRepository.setProgress({
      userId,
      courseId: courseId as string,
      contentBlockId,
      currentTime,
      seen,
    });

    // Update local progress data state
    setProgressData((prevData) => {
      const existingEntryIndex = prevData?.findIndex(
        (entry) => entry.contentBlockId === contentBlockId
      );
      if (existingEntryIndex !== undefined && existingEntryIndex >= 0) {
        // Update existing entry
        const updatedData = [...(prevData || [])];
        updatedData[existingEntryIndex] = {
          ...updatedData[existingEntryIndex],
          currentTime,
          seen:
            seen !== undefined ? seen : updatedData[existingEntryIndex].seen,
        };
        return updatedData;
      } else {
        // Add new entry
        return [
          ...(prevData || []),
          {
            id: `${userId}_${courseId}_${contentBlockId}`,
            userId,
            courseId: courseId as string,
            contentBlockId,
            currentTime,
            seen: true,
          },
        ];
      }
    });
  };

  const changeSeenStatus = async (
    contentBlockId: string,
    newStatus: boolean
  ) => {
    if (!user || !courseId || !school) {
      localLog("Cannot mark as seen: missing user, school or courseId");
      return;
    }
    const userId = user.uid;

    progressRepository.setProgress({
      userId,
      courseId: courseId as string,
      contentBlockId,
      seen: newStatus,
    });
    // Update local progress data state
    setProgressData((prevData) => {
      const existingEntryIndex = prevData?.findIndex(
        (entry) => entry.contentBlockId === contentBlockId
      );
      if (existingEntryIndex !== undefined && existingEntryIndex >= 0) {
        // Update existing entry
        const updatedData = [...(prevData || [])];
        updatedData[existingEntryIndex] = {
          ...updatedData[existingEntryIndex],
          seen: newStatus,
        };
        return updatedData;
      } else {
        // Add new entry
        return [
          ...(prevData || []),
          {
            id: `${userId}_${courseId}_${contentBlockId}`,
            userId,
            courseId: courseId as string,
            contentBlockId,
            currentTime: 0,
            seen: newStatus,
          },
        ];
      }
    });
  };

  const loadLastSeen = useCallback(async () => {
    if (!user || !courseId) {
      localLog("User or courseId not available yet");
      return;
    }
    const lastSeeBlockId = await lastSeenRepository.getCourseLastSeen(
      user.uid,
      courseId as string
    );
    if (lastSeeBlockId) {
      setLastSeenBlockId(lastSeeBlockId);
      localLog("Loaded last seen block ID:", lastSeeBlockId);
    }
  }, [user, courseId]);

  useEffectOnce(() => {
    loadLastSeen();
  }, []);

  const value: VideoPlayerContextType = {
    autoPlay,
    jumpToLastWatchEnable,
    changeSetting,
    blockChangeTrigger,
    setBlockChangeTrigger,
    handleBlockChange,
    sendProgressEvent,
    triggerJumpToTime,
    setTriggerJumpToTime,
    progressData,
    setProgressData,
    changeSeenStatus,
    lastSeenBlockId,
  };

  return (
    <VideoPlayerContext.Provider value={value}>
      {children}
    </VideoPlayerContext.Provider>
  );
}

export function useVideoPlayer() {
  const context = useContext(VideoPlayerContext);
  if (context === undefined) {
    throw new Error("useVideoPlayer must be used within a VideoPlayerProvider");
  }
  return context;
}

export default VideoPlayerProvider;
